var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';
import T from '../../../app/Locale';
import Button from '../../Components/ComponentButton';
import Modal from '../../Components/ComponentModal';
import Message from '../../ComponentMessage';
function SelectPartnersModal({ title, description, partners, existingPartners = [], teaser = false, cta, showNone = true, redirect = null }) {
    const [selectedPartners, setSelectedPartners] = useState(existingPartners.filter((partner) => partner.isPriorityPartner));
    const [consents, setConsents] = useState([]);
    const [errors, setErrors] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    /**
     * On first render, make sure all consent checkboxes are displayed for existing partners.
     */
    useEffect(() => {
        let newConsents = consents.slice();
        selectedPartners.forEach((partner) => {
            const consentsForPartner = getPartnerConsents(partner, true);
            newConsents = newConsents.concat(consentsForPartner);
        });
        setConsents(newConsents);
    }, []);
    const toggleNone = () => {
        setSelectedPartners([]);
        setConsents([]);
    };
    /**
     * Toggle selection of a partner
     *
     * @param {object} partner
     * @param {bool} toggle
     */
    const togglePartner = (partner, toggle) => {
        let newPartners;
        let newConsents;
        const consentsForPartner = getPartnerConsents(partner);
        if (toggle) {
            newPartners = selectedPartners.slice();
            newPartners.push(partner);
            newConsents = consents.slice().concat(consentsForPartner);
        }
        else {
            const consentsForPartnerIds = consentsForPartner.map((consent) => consent.id);
            newPartners = selectedPartners.slice().filter((selectedPartner) => selectedPartner.id !== partner.id);
            newConsents = consents.slice().filter((consent) => consentsForPartnerIds.indexOf(consent.id) === -1);
        }
        setSelectedPartners(newPartners);
        setConsents(newConsents);
    };
    /**
     * Get all consents for a partner visible for the modal.
     *
     * @param {object} partner
     * @param {boolean} startAccepted
     * @returns {array}
     */
    const getPartnerConsents = (partner, startAccepted = false) => {
        const consents = partner.consent
            ? partner.consent.consents.filter((consent) => consent.visible.indexOf('SelectPartnerModal') > -1)
            : [];
        return consents.map((consent, index) => {
            consent.key = `${partner.id}_${index}`;
            consent.accepted = startAccepted;
            return consent;
        });
    };
    /**
     * Toggle acceptance of a consent
     *
     * @param {string} consentId
     */
    const toggleConsent = (consentId) => {
        const newConsents = consents.slice();
        const consentToUpdate = newConsents.find((consent) => consent.id === consentId);
        consentToUpdate.accepted = !consentToUpdate.accepted;
        setConsents(newConsents);
    };
    const save = () => __awaiter(this, void 0, void 0, function* () {
        const newErrors = {};
        // If any consent are missing acceptance, flag a consent error.
        if (consents.find((consent) => !consent.accepted)) {
            newErrors.consent = T.translate('partners.error');
        }
        setErrors(newErrors);
        if (Object.keys(newErrors).length === 0) {
            setIsSaving(true);
            try {
                yield axios.post('/api/user/save-partners', { partners: selectedPartners.map((partner) => partner.id) });
                if (redirect) {
                    window.location.href = redirect;
                }
                else {
                    window.location.reload();
                }
            }
            catch (e) {
                if (e.response.status === 400) {
                    setErrors({ save: T.translate('partners.errorSaveOnboardingPartner') });
                }
                else {
                    setErrors({ save: T.translate('partners.errorSave') });
                }
                setIsSaving(false);
            }
        }
    });
    return (_jsxs("div", { children: [errors.save && _jsx(Message, { message: errors.save, isSuccessful: false }), _jsx(Modal, Object.assign({ id: 'modal-partner', fastRender: true, isWide: true }, { children: _jsxs("div", { children: [_jsx("h2", { children: title }), teaser && _jsx("h4", { children: teaser }), _jsx("p", { children: description }), _jsxs("div", Object.assign({ className: 'row reveal-partner__content' }, { children: [showNone && (_jsx("div", Object.assign({ className: 'columns small-12 medium-4' }, { children: _jsxs("div", Object.assign({ className: 'reveal-partner__item bg-gray-f6' }, { children: [_jsx("h5", { children: T.translate('misc.none') }), _jsx("p", Object.assign({ className: 'help-text' }, { children: T.translate('partners.none') })), _jsxs("div", Object.assign({ className: 'reveal-partner__checkbox tailwind-reset text-center' }, { children: [_jsx("input", { id: 'none', type: 'checkbox', onChange: toggleNone, checked: selectedPartners.length === 0 }), _jsx("label", Object.assign({ htmlFor: 'none', className: 'button hollow green' }, { children: T.translate('misc.choose') }))] }))] })) }))), partners.map((partner) => (_jsx("div", Object.assign({ className: 'columns small-12 medium-4' }, { children: _jsxs("div", Object.assign({ className: 'reveal-partner__item bg-gray-f6' }, { children: [_jsx("img", { src: `/files/images/partner_onboarding/${partner.logo}`, alt: partner.name }), _jsx("h5", { children: partner.name }), _jsx("p", Object.assign({ className: 'help-text' }, { children: partner.priority_users_advantages })), _jsxs("div", Object.assign({ className: 'reveal-partner__checkbox tailwind-reset text-center' }, { children: [_jsx("input", { id: partner.id, type: 'checkbox', checked: selectedPartners.some((selectedPartner) => selectedPartner.id === partner.id), onChange: (event) => togglePartner(partner, event.target.checked) }), _jsx("label", Object.assign({ htmlFor: partner.id, className: 'button hollow green' }, { children: T.translate('misc.choose') }))] }))] })) }), partner.id)))] })), _jsxs("div", Object.assign({ className: 'row' }, { children: [_jsx("div", Object.assign({ className: 'columns small-12 medium-6 medium-offset-3' }, { children: consents.map((consent) => (_jsxs("div", Object.assign({ className: 'input-container input-container--checkbox' }, { children: [_jsx("input", { id: consent.key, type: 'checkbox', checked: consent.accepted, onChange: () => toggleConsent(consent.id) }), _jsxs("label", Object.assign({ htmlFor: consent.key }, { children: [consent.text, (errors.consent && !consent.accepted) && _jsx("p", Object.assign({ className: 'is-invalid-label' }, { children: consent.error }))] }))] }), consent.id))) })), _jsx("div", Object.assign({ className: 'columns small-12' }, { children: _jsx("div", Object.assign({ className: 'text-center reveal-partner__actions' }, { children: _jsx(Button, { title: cta, isLoading: isSaving, onClick: save, classes: 'green' }) })) }))] })), _jsx("br", {}), _jsx("br", {})] }) }))] }));
}
SelectPartnersModal.propTypes = {
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    teaser: PropTypes.string,
    cta: PropTypes.string.isRequired,
    showNone: PropTypes.bool,
    partners: PropTypes.array.isRequired,
    existingPartners: PropTypes.array,
    redirect: PropTypes.string
};
export default SelectPartnersModal;
